/**
 * D-326 - the "שיחות" chat screen: pure rules (no React, no network).
 *
 * The list is built on the existing grouped conversations (a group = one chat per exact participant
 * set). A chat of two people is a 1:1 (the tab of the other side: an OTE employee -> "עובדים", a
 * customer's person -> "לקוחות"); more than two is a group ("קבוצות"). "אחרונים" is all of them.
 */
export type ChatTab = 'recent' | 'employees' | 'customers' | 'groups';
export const CHAT_TABS: readonly ChatTab[] = ['recent', 'employees', 'customers', 'groups'];

export interface ChatParticipant { id: string; name: string; role: string }
export interface ChatGroupLike {
  participants: readonly ChatParticipant[];
  total_unread: number;
  last_activity_at?: string | null;
  tickets: ReadonlyArray<{ id: string; title: string; status?: string; last_comment_at?: string | null }>;
}

export const isCustomerRole = (role: string | null | undefined): boolean => role === 'customer_viewer' || role === 'customer_manager';

export const parseChatTab = (v: string | null | undefined): ChatTab => ((CHAT_TABS as readonly string[]).includes(v ?? '') ? (v as ChatTab) : 'recent');

/** Two people = a 1:1, more = a group. */
export function isDirectChat(g: Pick<ChatGroupLike, 'participants'>): boolean {
  return g.participants.length <= 2;
}

/** The other side of a 1:1 (null in a group, or alone). */
export function otherParty(g: Pick<ChatGroupLike, 'participants'>, meId: string | null | undefined): ChatParticipant | null {
  if (!isDirectChat(g)) return null;
  return g.participants.find((p) => p.id !== meId) ?? null;
}

/** Which tab a chat belongs to besides "אחרונים". */
export function tabOfChat(g: Pick<ChatGroupLike, 'participants'>, meId: string | null | undefined): Exclude<ChatTab, 'recent'> {
  if (!isDirectChat(g)) return 'groups';
  const other = otherParty(g, meId);
  return other && isCustomerRole(other.role) ? 'customers' : 'employees';
}

export function chatsForTab<G extends Pick<ChatGroupLike, 'participants'>>(groups: readonly G[], tab: ChatTab, meId: string | null | undefined): G[] {
  if (tab === 'recent') return [...groups];
  return groups.filter((g) => tabOfChat(g, meId) === tab);
}

/** The unread counter of the "אחרונים" tab / the menu: the sum over the chats. */
export function totalUnread(groups: ReadonlyArray<Pick<ChatGroupLike, 'total_unread'>>): number {
  return groups.reduce((n, g) => n + (Number(g.total_unread) || 0), 0);
}

/** The counter shown in the menu and on the tab (D-326, like WhatsApp): the NUMBER OF CHATS that have unread messages. */
export function unreadChatsCount(groups: ReadonlyArray<Pick<ChatGroupLike, 'total_unread'>>): number {
  return groups.filter((g) => (Number(g.total_unread) || 0) > 0).length;
}

/** A ticket title needs 3+ characters (server rule); a 1:1 has no subject, so it is the other person's name. */
export function directChatTitle(otherName: string): string {
  const n = (otherName ?? '').trim();
  return n.length >= 3 ? n : `${n} - שיחה`;
}

/** Search over name / subject / organisation (case-insensitive). */
export function matchesChatSearch(haystack: ReadonlyArray<string | null | undefined>, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return haystack.some((h) => (h ?? '').toLowerCase().includes(q));
}

export interface PersonRow<U extends { id: string }> {
  user: U;
  /** The 1:1 chat ticket already open with this person, if any (the click enters it: no duplicates). */
  ticketId: string | null;
  unread: number;
  lastAt: number;
  online: boolean;
}

const ts = (v: string | null | undefined): number => {
  const t = v ? Date.parse(v) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

/** People of a tab (employees, or one customer's logins) with the state of their 1:1 chat. Me excluded. */
export function personRows<U extends { id: string; name: string }>(
  people: readonly U[],
  groups: readonly ChatGroupLike[],
  meId: string | null | undefined,
  onlineIds: ReadonlySet<string>,
): PersonRow<U>[] {
  const direct = new Map<string, ChatGroupLike>();
  for (const g of groups) {
    const o = otherParty(g, meId);
    if (!o) continue;
    const prev = direct.get(o.id);
    if (!prev || ts(g.last_activity_at) > ts(prev.last_activity_at)) direct.set(o.id, g);
  }
  return people
    .filter((u) => u.id !== meId)
    .map((user) => {
      const g = direct.get(user.id);
      const latest = g?.tickets.reduce((b, t) => (ts(t.last_comment_at) > ts(b.last_comment_at) ? t : b), g.tickets[0]);
      return { user, ticketId: latest?.id ?? null, unread: g?.total_unread ?? 0, lastAt: ts(g?.last_activity_at), online: onlineIds.has(user.id) };
    })
    .sort((a, b) => (b.unread > 0 ? 1 : 0) - (a.unread > 0 ? 1 : 0) || b.lastAt - a.lastAt || a.user.name.localeCompare(b.user.name, 'he'));
}

/** The line under a chat's name: "X מקליד/ה..." for one, several joined for more. */
export function typingText(names: readonly string[], t: (k: string, o?: Record<string, unknown>) => string): string {
  if (names.length === 0) return '';
  if (names.length === 1) return t('chatScreen.typingOne', { name: names[0] });
  return t('chatScreen.typingMany', { names: names.join(', ') });
}
